import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { apiClient } from "@/shared/services/apiClient";
import { useToast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/shared/contexts/AuthContext";

export default function DeleteAccountPage() {
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"email" | "otp" | "success">("email");
  const [loading, setLoading] = useState(false);
  const otpSentRef = useRef(false); // Use ref instead of state to prevent re-renders

  // If user is logged in, skip email step and auto-populate email
  useEffect(() => {
    if (isAuthenticated && user?.email && !otpSentRef.current) {
      otpSentRef.current = true; // Set immediately to prevent duplicate calls
      setEmail(user.email);
      setStep("otp");
      // Auto-send OTP for logged-in users (only once)
      handleSendOTPForAuthenticatedUser(user.email);
    }
  }, [isAuthenticated, user?.email]);

  // Send OTP automatically for authenticated users
  const handleSendOTPForAuthenticatedUser = async (userEmail: string) => {
    setLoading(true);
    try {
      const response = await apiClient.post<{ ok: boolean; message?: string }>(
        "/auth/send-otp",
        { email: userEmail, purpose: "account_deletion" }
      );

      if (response.ok) {
        toast({
          title: "Verification Code Sent",
          description: "Check your email for the verification code",
        });
      } else {
        throw new Error(response.message || "Failed to send verification code");
      }
    } catch (error: any) {
      console.error("Send OTP error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send verification code",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post<{ ok: boolean; message?: string }>(
        "/auth/send-otp",
        { email, purpose: "account_deletion" }
      );

      if (response.ok) {
        toast({
          title: "Verification Code Sent",
          description: "Check your email for the verification code",
        });
        setStep("otp");
      } else {
        throw new Error(response.message || "Failed to send verification code");
      }
    } catch (error: any) {
      console.error("Send OTP error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send verification code",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndDelete = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter the 6-digit verification code",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // First verify OTP
      const verifyResponse = await apiClient.post<{ ok: boolean; message?: string }>(
        "/auth/verify-otp",
        { email, otp, purpose: "account_deletion" }
      );

      if (!verifyResponse.ok) {
        throw new Error(verifyResponse.message || "Invalid verification code");
      }

      // Then request deletion (this would need to be implemented as a public endpoint)
      const deleteResponse = await apiClient.post<{
        ok: boolean;
        message?: string;
        scheduled_deletion_at?: string;
      }>(
        "/auth/request-deletion-public",
        { email, otp }
      );

      if (deleteResponse.ok) {
        setStep("success");
      } else {
        throw new Error(deleteResponse.message || "Failed to schedule account deletion");
      }
    } catch (error: any) {
      console.error("Delete account error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to verify code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Delete Your Account
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            We're sorry to see you go. This process is permanent and cannot be undone.
          </p>
        </div>

        {step === "email" && (
          <Card>
            <CardHeader>
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-3">
                  <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <CardTitle className="text-center">Request Account Deletion</CardTitle>
              <CardDescription className="text-center">
                Enter your email address to begin the account deletion process
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="mb-6 border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
                <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                <AlertDescription className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Important:</strong> Deleting your account will permanently remove:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>All your call recordings and history</li>
                    <li>Your profile and account settings</li>
                    <li>Team memberships and permissions</li>
                    <li>Scheduled calls and calendar events</li>
                    <li>Subscription and billing information</li>
                  </ul>
                  <p className="mt-2">You will have a <strong>90-day grace period</strong> to cancel this request.</p>
                </AlertDescription>
              </Alert>

              <form onSubmit={handleSendOTP} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Verification Code"}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
                Already logged in?{" "}
                <Link to="/settings" className="text-primary hover:underline">
                  Go to Settings
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "otp" && (
          <Card>
            <CardHeader>
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900/20 p-3">
                  <Mail className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <CardTitle className="text-center">Verify Your Email</CardTitle>
              <CardDescription className="text-center">
                We've sent a 6-digit code to {email}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerifyAndDelete} className="space-y-4">
                <div>
                  <Label htmlFor="otp">Verification Code</Label>
                  <Input
                    id="otp"
                    type="text"
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength={6}
                    required
                    disabled={loading}
                    className="text-center text-2xl tracking-widest"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? "Verifying..." : "Verify and Schedule Deletion"}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setStep("email")}
                  disabled={loading}
                >
                  Use a Different Email
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === "success" && (
          <Card>
            <CardHeader>
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-3">
                  <svg
                    className="h-8 w-8 text-green-600 dark:text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <CardTitle className="text-center">Deletion Scheduled</CardTitle>
              <CardDescription className="text-center">
                Your account has been scheduled for deletion
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
                <AlertDescription className="text-sm text-green-800 dark:text-green-200">
                  Your account will be permanently deleted in <strong>90 days</strong>.
                  <br /><br />
                  During this period, you can:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Continue using your account normally</li>
                    <li>Cancel the deletion request at any time</li>
                    <li>All your data will remain intact until the deletion date</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <div className="pt-4">
                <Link to="/">
                  <Button className="w-full">
                    Return to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
          <p>
            Need help?{" "}
            <Link to="/customer-support" className="text-primary hover:underline">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
